import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SrIcon } from "@/components/ui/SrIcon";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import {
  useSystemConfigNumber,
  useUpdateSystemConfigNumber,
} from "@/hooks/use-system-config";
import { StoragePricingTiersPanel } from "@/components/settings/StoragePricingTiersPanel";

export default function OkonomiSettings() {
  const { activeUser } = useImpersonation();
  const canManageSettings =
    activeUser?.role === "admin" || activeUser?.role === "superadmin";

  const { data: hourlyRate, isLoading: hourlyRateLoading } =
    useSystemConfigNumber("hourly_rate");
  const updateHourlyRate = useUpdateSystemConfigNumber();
  const [hourlyRateInput, setHourlyRateInput] = useState<string>("");

  const { data: provisionRate, isLoading: provisionRateLoading } =
    useSystemConfigNumber("provision_rate");
  const updateProvisionRate = useUpdateSystemConfigNumber();
  const [provisionRateInput, setProvisionRateInput] = useState<string>("");

  const { data: overheadPct, isLoading: overheadLoading } =
    useSystemConfigNumber("default_overhead_pct");
  const updateOverhead = useUpdateSystemConfigNumber();
  const [overheadInput, setOverheadInput] = useState<string>("");

  useEffect(() => {
    if (hourlyRate != null) setHourlyRateInput(String(hourlyRate));
  }, [hourlyRate]);

  useEffect(() => {
    if (provisionRate != null) setProvisionRateInput(String(provisionRate));
  }, [provisionRate]);

  useEffect(() => {
    if (overheadPct != null) setOverheadInput(String(overheadPct));
  }, [overheadPct]);

  function handleSaveHourlyRate() {
    const parsed = Number(hourlyRateInput);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      toast.error("Oppgi en gyldig timesats (kroner, over 0)");
      return;
    }
    updateHourlyRate.mutate(
      { key: "hourly_rate", value: parsed },
      {
        onSuccess: () =>
          toast.success(`Estimert timesats oppdatert til ${parsed} kr/t`),
        onError: (err) =>
          toast.error(`Kunne ikke lagre: ${(err as Error).message}`),
      },
    );
  }

  function handleSaveProvisionRate() {
    const parsed = Number(provisionRateInput);
    if (!Number.isFinite(parsed) || parsed < 0 || parsed > 100) {
      toast.error("Oppgi et gyldig prosent-tall (0–100)");
      return;
    }
    updateProvisionRate.mutate(
      { key: "provision_rate", value: parsed },
      {
        onSuccess: () =>
          toast.success(`Provisjonssats oppdatert til ${parsed}%`),
        onError: (err) =>
          toast.error(`Kunne ikke lagre: ${(err as Error).message}`),
      },
    );
  }

  function handleSaveOverhead() {
    const parsed = Number(overheadInput);
    if (!Number.isFinite(parsed) || parsed < 0 || parsed > 100) {
      toast.error("Oppgi et gyldig prosent-tall (0–100)");
      return;
    }
    updateOverhead.mutate(
      { key: "default_overhead_pct", value: parsed },
      {
        onSuccess: () =>
          toast.success(`Overheadpåslag oppdatert til ${parsed}%`),
        onError: (err) =>
          toast.error(`Kunne ikke lagre: ${(err as Error).message}`),
      },
    );
  }

  if (!canManageSettings) {
    return (
      <div className="page-container">
        <p className="text-sm text-muted-foreground">Du har ikke tilgang.</p>
      </div>
    );
  }

  return (
    <div className="page-container animate-fade-in space-y-6">
      <h1 className="font-display font-semibold text-[clamp(28px,3vw,40px)] tracking-[-0.01em] flex items-center gap-2 mb-6">
        <SrIcon name="coins" className="h-6 w-6" />
        Økonomi
      </h1>

      {/* Estimert Timesats */}
      <Card data-testid="timesats-card">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <SrIcon name="coins" className="h-4 w-4 text-muted-foreground" />
            <h2 className="font-display text-base">Estimert Timesats</h2>
          </div>
          <div className="p-4 rounded-lg border border-border/50 bg-card/50">
            <p className="text-sm text-muted-foreground mb-3">
              Standard timesats som brukes i tilbudsgeneratoren. Kan overstyres per tilbud.
            </p>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <Label htmlFor="hourly-rate-input" className="w-full sm:w-auto text-sm">
                Estimert timesats
              </Label>
              <Input
                id="hourly-rate-input"
                type="number"
                min={0}
                step={50}
                value={hourlyRateInput}
                onChange={(e) => setHourlyRateInput(e.target.value)}
                disabled={hourlyRateLoading || updateHourlyRate.isPending}
                className="w-full sm:w-32"
              />
              <span className="text-sm text-muted-foreground">kr/t</span>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSaveHourlyRate}
                disabled={
                  hourlyRateLoading ||
                  updateHourlyRate.isPending ||
                  hourlyRateInput === String(hourlyRate ?? "")
                }
              >
                {updateHourlyRate.isPending && (
                  <SrIcon name="loader2" className="w-3 h-3 mr-1 animate-spin" />
                )}
                Lagre
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Provisjonssats */}
      <Card data-testid="provisjonssats-card">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <SrIcon name="percent" className="h-4 w-4 text-muted-foreground" />
            <h2 className="font-display text-base">Provisjonssats</h2>
          </div>
          <div className="p-4 rounded-lg border border-border/50 bg-card/50">
            <p className="text-sm text-muted-foreground mb-3">
              Andel av reell GEEK som fordeles som provisjon mellom prosjekteierne.
              Deles likt mellom selgere knyttet til prosjektet.
            </p>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <Label htmlFor="provision-rate-input" className="w-full sm:w-auto text-sm">
                Provisjonssats
              </Label>
              <Input
                id="provision-rate-input"
                type="number"
                min={0}
                max={100}
                step={0.5}
                value={provisionRateInput}
                onChange={(e) => setProvisionRateInput(e.target.value)}
                disabled={provisionRateLoading || updateProvisionRate.isPending}
                className="w-full sm:w-32"
              />
              <span className="text-sm text-muted-foreground">% av GEEK</span>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSaveProvisionRate}
                disabled={
                  provisionRateLoading ||
                  updateProvisionRate.isPending ||
                  provisionRateInput === String(provisionRate ?? "")
                }
              >
                {updateProvisionRate.isPending && (
                  <SrIcon name="loader2" className="w-3 h-3 mr-1 animate-spin" />
                )}
                Lagre
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Overheadpåslag */}
      <Card data-testid="overhead-card">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <SrIcon name="percent" className="h-4 w-4 text-muted-foreground" />
            <h2 className="font-display text-base">Overheadpåslag</h2>
          </div>
          <div className="p-4 rounded-lg border border-border/50 bg-card/50">
            <p className="text-sm text-muted-foreground mb-3">
              Standard overheadpåslag som settes på nye tilbud. Kan overstyres
              per tilbud.
            </p>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <Label htmlFor="overhead-input" className="w-full sm:w-auto text-sm">
                Påslag
              </Label>
              <Input
                id="overhead-input"
                type="number"
                min={0}
                max={100}
                step={0.5}
                value={overheadInput}
                onChange={(e) => setOverheadInput(e.target.value)}
                disabled={overheadLoading || updateOverhead.isPending}
                className="w-full sm:w-24"
              />
              <span className="text-sm text-muted-foreground">%</span>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSaveOverhead}
                disabled={
                  overheadLoading ||
                  updateOverhead.isPending ||
                  overheadInput === String(overheadPct ?? "")
                }
              >
                {updateOverhead.isPending && (
                  <SrIcon name="loader2" className="w-3 h-3 mr-1 animate-spin" />
                )}
                Lagre
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Produktkatalog */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <SrIcon name="database" className="h-4 w-4 text-muted-foreground" />
            <h2 className="font-display text-base">Produktkatalog</h2>
          </div>
          <div className="p-4 rounded-lg border border-border/50 bg-card/50 flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Rediger produktlinjer og tilbudsmaler som brukes i tilbudsgeneratoren.
            </p>
            <Link to="/innstillinger/produktkatalog">
              <Button variant="outline" size="sm">
                Gå til produktkatalog
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* VIB-1308: lagring — trinnpriser */}
      <StoragePricingTiersPanel />
    </div>
  );
}
